import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Logo } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/community");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Sign in failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      {/* Brand panel */}
      <div className="relative hidden flex-col justify-between overflow-hidden bg-[#0D0F12] p-12 text-white lg:flex">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1509391366360-2e959784a276?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D0F12] via-[#0D0F12]/70 to-transparent" />
        <div className="relative">
          <Logo wm={28} tone="dark" />
        </div>
        <div className="relative max-w-md">
          <h1 className="font-heading text-[34px] font-bold leading-tight">
            The private CPD hub for the UK energy broking industry.
          </h1>
          <p className="mt-4 text-[15px] leading-relaxed text-white/70">
            Structured learning, tracked CPD hours and a members-only community, built for
            brokers and introducers who take their craft seriously.
          </p>
        </div>
        <div className="relative text-sm text-white/50">Invitation-only · trueacademy.uk</div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <Logo wm={28} tone="light" />
          </div>
          <h2 className="font-heading text-[24px] font-semibold">Sign in</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Enter your details to access the academy.
          </p>

          <form onSubmit={submit} className="mt-7 space-y-4" data-testid="login-form">
            <div>
              <label className="mb-1.5 block text-sm font-medium">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-11 w-full rounded-lg border border-input bg-card px-3.5 text-sm outline-none focus:ring-2 focus:ring-ring"
                placeholder="you@company.co.uk"
              />
            </div>
            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <label className="block text-sm font-medium">Password</label>
                <Link to="/forgot-password" className="text-xs text-[#1779AC] hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
              </div>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-11 w-full rounded-lg border border-input bg-card px-3.5 text-sm outline-none focus:ring-2 focus:ring-ring"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div data-testid="login-error" className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}

            <Button type="submit" disabled={loading} data-testid="login-submit-button" className="btn-brand h-11 w-full">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <p className="mt-5 text-xs leading-relaxed text-muted-foreground">
            New members receive a one-click invitation link by email, so no password is needed on first entry.
          </p>

        </div>
      </div>
    </div>
  );
}
